import React, { useState, useEffect } from 'react';

import { Modal as ModalAntD } from 'antd';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { rejectionMessage, rejectionOf } from './inviteRejection';
import { countdownFrom } from './tokenCountdown';
import { wrongTokenMessage } from './tokenKind';

import {
  acceptInvite,
  acceptInviteById,
  currentActor,
  validateInvite,
} from '../../../../hooks/interoperability';
import { InviteReview } from '../../../../types/responses/Interoperability';
import { initialsOf } from '../contractRules';

const Label = styled.div`
  color: #4a5462;
  font-size: 11.5px;
  margin-bottom: 6px;
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
`;

const TokenInput = styled.input<{ invalid: boolean }>`
  flex: 1;
  min-width: 0;
  border: 1px solid ${({ invalid }) => (invalid ? '#c8352f' : '#d9dce1')};
  border-radius: 6px;
  padding: 9px 11px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px;
  color: #12263f;
  outline: none;
`;

const ValidateButton = styled.button`
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 9px 15px;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    background: #a9b4c2;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ error: boolean }>`
  margin-top: 7px;
  min-height: 16px;
  font-size: 11px;
  line-height: 1.5;
  color: ${({ error }) => (error ? '#b02525' : '#8a9099')};
`;

const PartnerHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #eef2f7;
  color: ${({ theme }) => theme.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
`;

const PartnerName = styled.div`
  font-weight: 600;
  font-size: 13.5px;
  color: #12263f;
`;

const PartnerMeta = styled.div`
  color: #8a9099;
  font-size: 11px;
`;

const ValidPill = styled.span`
  margin-left: auto;
  flex: none;
  background: #e7f6ed;
  color: #1e7a45;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 11px;
`;

const ContractLine = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #f0f2f4;
  color: #3d4653;
  font-size: 12px;
`;

const ContractHint = styled.div`
  color: #8a9099;
  font-size: 11px;
  margin-top: 2px;
`;

const Direction = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 12px;
`;

const Arrow = styled.div`
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #f6f8fa;
  color: ${({ theme }) => theme.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`;

const DirectionText = styled.div`
  font-size: 12px;
  color: #3d4653;
`;

const Authorize = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed #e3e6ea;
  cursor: pointer;
  font-size: 12px;
  color: #3d4653;
`;

const Check = styled.span<{ $on: boolean }>`
  flex: none;
  width: 17px;
  height: 17px;
  border-radius: 4px;
  border: 1px solid ${({ $on, theme }) => ($on ? theme.primary : '#c6cbd3')};
  background: ${({ $on, theme }) => ($on ? theme.primary : '#fff')};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
`;

const Done = styled.div`
  text-align: center;
  padding: 8px 0 4px;
`;

const DoneMark = styled.div`
  margin: 0 auto 10px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e7f6ed;
  color: #1e7a45;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const DoneTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #12263f;
`;

const DoneText = styled.p`
  margin: 8px auto 0;
  max-width: 380px;
  color: #5b6472;
  font-size: 12px;
  line-height: 1.5;
`;

const Foot = styled.div`
  padding: 14px 20px;
  border-top: 1px solid #e9ebee;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const CancelButton = styled.button`
  background: #fff;
  color: #4a5462;
  border: 1px solid #d9dce1;
  border-radius: 6px;
  padding: 9px 16px;
  cursor: pointer;
`;

const ConnectButton = styled.button`
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 9px 16px;
  cursor: pointer;

  &:disabled {
    background: #a9b4c2;
    cursor: not-allowed;
  }
`;

// O @types do antd 4.10 nao declara children no Modal; o repo contorna com este
// alias em FinancialDrawerDetails.tsx:54 e CompanySelect.tsx:12.
const Modal: any = ModalAntD;

type Phase = 'input' | 'review' | 'done';

export type AcceptInviteModalProps = {
  tenantId: string;

  /**
   * Convite que veio do banner de pendentes, quando a pessoa chegou por ali.
   * Serve só para dizer de quem se espera o token: aceitar exige o token, que
   * este lado não tem — ele foi entregue fora do sistema.
   */
  expectedFrom?: InviteReview | null;

  onClose: () => void;

  /** Contrato aceito: a tela de trás recarrega. */
  onAccepted: () => void;
};

/**
 * Aceitar convite (MV2-1716).
 *
 * Três fases: colar o token, revisar o que vai ser compartilhado, confirmar.
 *
 * O token vive só no estado deste componente. Não vai para a URL, nem para
 * localStorage, nem para console — a validação e o aceite mandam ele no corpo do
 * POST. Depois do aceite o campo é esvaziado: ele já não vale mais nada, e
 * guardá-lo na tela só deixaria um segredo gasto à mostra.
 */
export const AcceptInviteModal: React.FC<AcceptInviteModalProps> = ({
  tenantId,
  expectedFrom,
  onClose,
  onAccepted,
}) => {
  /**
   * Convite que já veio identificado entra direto na revisão.
   *
   * Quem chega pelo banner ou pela aba Convites tem o convite endereçado à própria
   * conta e está autenticado. Pedir o token ali é pedir para provar o que o recorte da
   * requisição já prova — e como o token é entregue fora do sistema, quem revisa por
   * ali pode nem tê-lo em mãos, o que tornava o caminho do banner um beco sem saída.
   */
  const [phase, setPhase] = useState<Phase>(expectedFrom ? 'review' : 'input');
  const [token, setToken] = useState('');
  const [review, setReview] = useState<InviteReview | null>(expectedFrom || null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  /**
   * O convite pode chegar depois da montagem.
   *
   * A pagina renderiza este modal fora da guarda de carga, e o `expectedFrom` sai de
   * `receivedInvites` — vazio enquanto a carga inicial corre. Recarregar com
   * ?action=accept&partner=X montava com `phase = 'input'`, e nada sincronizava depois: o
   * cabecalho passava a citar o parceiro, porque le a prop, enquanto a revisao e o botao
   * Conectar ficavam inalcancaveis. Quem chega pelo link nao tem o token em maos.
   *
   * So entra na revisao se a pessoa ainda nao mexeu: com token digitado, sincronizar
   * seria puxar o tapete de quem esta no meio de uma acao.
   */
  useEffect(() => {
    if (expectedFrom && phase === 'input' && !token) {
      setReview(expectedFrom);
      setPhase('review');
    }
    // `phase` e `token` ficam fora das dependencias de proposito: o efeito reage ao
    // convite chegando, e nao a cada tecla digitada no campo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedFrom]);

  const partnerName = review?.source_tenant_name || expectedFrom?.source_tenant_name || '';

  const validate = async () => {
    const typed = token.trim();

    if (!typed) {
      return;
    }

    // O token da conta colado aqui e o engano previsivel do modulo. Recusar antes de
    // enviar diz o que esta errado, em vez de gastar uma tentativa do limitador para
    // receber de volta "confira se copiou o codigo completo".
    const mismatch = wrongTokenMessage(typed, 'INVITE');

    if (mismatch) {
      setError(mismatch);
      return;
    }

    try {
      setBusy(true);
      setError('');

      const found = await validateInvite(tenantId, typed);

      setReview(found);
      setPhase('review');
    } catch (requestError) {
      const rejection = rejectionOf(requestError);

      setError(
        rejection
          ? rejectionMessage(rejection.rejection, rejection.reference_date)
          : T('Could not check the token now. Try again in a moment.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const connect = async () => {
    try {
      setBusy(true);
      setError('');

      // Sem token quando o convite já está identificado: aceita por id, que é o
      // caminho que o backend oferece para exatamente este caso.
      if (review?.invite_id && !token.trim()) {
        await acceptInviteById(tenantId, review.invite_id);
      } else {
        await acceptInvite(tenantId, token.trim(), currentActor());
      }

      // Token gasto: sai da memória da tela junto com a confirmação.
      setToken('');
      setPhase('done');
      onAccepted();
    } catch (requestError) {
      const rejection = rejectionOf(requestError);

      // O resumo na tela já não vale: o convite pode ter sido usado ou revogado
      // entre a revisão e o Conectar. Quem entrou pelo token volta para o campo;
      // quem entrou por um convite identificado fica na revisão com o erro, porque
      // mandá-lo para um campo de token que ele não tem seria um beco sem saída.
      setPhase(expectedFrom ? 'review' : 'input');
      setAuthorized(false);
      setError(
        rejection
          ? rejectionMessage(rejection.rejection, rejection.reference_date)
          : T('Could not accept the invite now. Try again in a moment.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const bidirectional = review?.contract_type === 'BIDIRECTIONAL';
  const expiry = review ? countdownFrom(review.expires_at) : null;

  return (
    /*
      Modal do antd, como os outros 30 do repo. Os overlays proprios que estavam
      aqui nao fechavam com Esc, nao prendiam o foco, nao tinham role="dialog" e
      cada um decidia se fechava no backdrop — tres comportamentos diferentes no
      mesmo modulo. O footer fica proprio porque os botoes dependem da fase.
    */
    <Modal
      visible
      width={520}
      title={T('Accept invite')}
      onCancel={onClose}
      footer={
        <Foot>
          <CancelButton type="button" onClick={onClose}>
            {phase === 'done' ? T('Close') : T('Cancel')}
          </CancelButton>

          {phase === 'review' && (
            <ConnectButton type="button" onClick={connect} disabled={!authorized || busy}>
              {T('Connect')}
            </ConnectButton>
          )}
        </Foot>
      }
    >
      <div>
          {phase === 'input' && (
            <div>
              <Label>
                {expectedFrom?.source_tenant_name
                  ? `${T('Paste the token sent by')} ${expectedFrom.source_tenant_name}`
                  : T('Paste the token your partner sent you')}
              </Label>

              <InputRow>
                <TokenInput
                  value={token}
                  invalid={!!error}
                  autoFocus
                  spellCheck={false}
                  autoComplete="off"
                  placeholder="move_itp_••••••••••••••••••••"
                  onChange={(event) => setToken(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      validate();
                    }
                  }}
                />
                <ValidateButton type="button" onClick={validate} disabled={busy || !token.trim()}>
                  {T('Validate')}
                </ValidateButton>
              </InputRow>

              <Message error={!!error}>
                {error ||
                  T(
                    'Single-use and time-limited. The token never appears in the URL — up to 5 attempts.',
                  )}
              </Message>
            </div>
          )}

          {phase === 'review' && review && (
            <div>
              <PartnerHead>
                <Avatar>{initialsOf(partnerName)}</Avatar>
                <div>
                  <PartnerName>{partnerName}</PartnerName>
                  <PartnerMeta>
                    {expiry && !expiry.expired
                      ? `${T('expires in')} ${expiry.text}`
                      : T('no longer valid')}
                  </PartnerMeta>
                </div>
                {/*
                  O selo so afirma validade enquanto ela vale (MV2-1811). Ele era
                  fixo, entao um convite que deixou de valer aparecia como "Token
                  valido" ao lado do proprio aviso de que nao vale mais — e o
                  resumo desta tela vem da lista carregada antes, que envelhece.
                  O motivo exato so o servidor sabe, e ele diz no Conectar.
                */}
                {(!expiry || !expiry.expired) && <ValidPill>{T('Valid token')}</ValidPill>}
              </PartnerHead>

              <ContractLine>
                {bidirectional
                  ? `⇄ ${T('Bidirectional contract')}`
                  : `→ ${T('One-way contract')}`}
                <ContractHint>
                  {bidirectional
                    ? T('both sides display each other stations')
                    : T('only your app starts displaying stations')}
                </ContractHint>
              </ContractLine>

              {/*
                A seta descreve para onde a estação vai em relação a quem lê: entra (←) ou sai
                (→). A convenção é a mesma na lista de conexões e no mapa.
              */}
              <Direction>
                <Arrow>←</Arrow>
                <DirectionText>
                  {T('Your app starts displaying')}{' '}
                  <strong>
                    {review.scope_station_count ?? 0} {T('stations')}
                  </strong>{' '}
                  {T('from')} {partnerName}
                  {review.scope_account_count
                    ? ` — ${review.scope_account_count} ${T('account(s)')}`
                    : ''}
                </DirectionText>
              </Direction>

              {bidirectional && (
                <Direction>
                  <Arrow>→</Arrow>
                  <DirectionText>
                    {partnerName} {T('starts displaying')}{' '}
                    <strong>
                      {review.target_station_count ?? 0} {T('of your stations')}
                    </strong>
                  </DirectionText>
                </Direction>
              )}

              <Authorize>
                <Check $on={authorized}>{authorized ? '✓' : ''}</Check>
                <input
                  type="checkbox"
                  checked={authorized}
                  onChange={(event) => setAuthorized(event.target.checked)}
                  style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                />
                {T('I authorize the sharing described above')}
              </Authorize>

              {error && <Message error>{error}</Message>}
            </div>
          )}

          {phase === 'done' && (
            <Done>
              <DoneMark>✓</DoneMark>
              <DoneTitle>
                {T('Connected with')} {partnerName}
              </DoneTitle>
              <DoneText>
                {T(
                  'The token was invalidated and cannot be used again. The contract is now active and the stations are already in your combined network.',
                )}
              </DoneText>
            </Done>
          )}
      </div>
    </Modal>
  );
};
